import React, { useEffect, useRef, useState } from "react";
import { Box } from "@mui/material";

// Lightweight IntersectionObserver-based reveal-on-scroll wrapper.
const Reveal = ({ children, delay = 0, sx, ...rest }) => {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    // Safety: if IntersectionObserver is unavailable, show immediately.
    if (typeof IntersectionObserver === "undefined") {
      setVisible(true);
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setVisible(true);
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.08, rootMargin: "0px 0px -40px 0px" }
    );
    observer.observe(el);

    // Fallback: never leave content permanently hidden. If the observer
    // hasn't fired within 1.2s (e.g. odd layout, print, or already in view
    // without a scroll event), reveal anyway.
    const fallback = setTimeout(() => setVisible(true), 1200);

    return () => {
      observer.disconnect();
      clearTimeout(fallback);
    };
  }, []);

  return (
    <Box
      ref={ref}
      className={`reveal${visible ? " is-visible" : ""}`}
      sx={{ transitionDelay: `${delay}ms`, ...sx }}
      {...rest}
    >
      {children}
    </Box>
  );
};

export default Reveal;
